import { formatISO } from 'date-fns';
import type { BandSet, DateKey, DayType, BandKey, HourRange, IsoWeekday, RateCard } from '../types';

/** A form value nobody filled in. Whitespace is empty — rate and spot inputs
 * reach here as raw strings, and a space is not an entry. */
export function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

/** `RadioRateCard.name` is blank-able, and by convention the reference card of a
 * price list is left unnamed. The default card is the standard one — vendors
 * publish it as "standard advertising" — while any other nameless card is just
 * missing its label. */
export const getRateCardName = (card: Pick<RateCard, 'name' | 'default'>): string =>
  card.name || (card.default ? 'Standard' : 'Unnamed');

export type BandGroup<T> = { dayType: DayType; bands: T[] };

export function getBandGroups<T>(bands: BandSet<T>): BandGroup<T>[] {
  return bands.day_type_split
    ? [
        { dayType: 'WD', bands: bands.WD },
        { dayType: 'WE', bands: bands.WE },
      ]
    : [{ dayType: 'ALL', bands: bands.ALL }];
}

/** Inverse of `getBandGroups`. A single `ALL` group is unsplit geometry;
 * anything else is a WD/WE split, so an empty split set stays split. */
export function toBandSet<T>(groups: BandGroup<T>[]): BandSet<T> {
  const of = (dayType: DayType): T[] => groups.find((group) => group.dayType === dayType)?.bands ?? [];
  return groups.some(({ dayType }) => dayType !== 'ALL')
    ? { day_type_split: true, WD: of('WD'), WE: of('WE') }
    : { day_type_split: false, ALL: of('ALL') };
}

/** Rebuild a band set with every band mapped, preserving the split. */
export function mapBandSet<T, U>(bands: BandSet<T>, map: (band: T, dayType: DayType) => U): BandSet<U> {
  return toBandSet(
    getBandGroups(bands).map(({ dayType, bands: groupBands }) => ({
      dayType,
      bands: groupBands.map((band) => map(band, dayType)),
    })),
  );
}

export function forEachBand<T>(bands: BandSet<T>, callback: (band: T, dayType: DayType, index: number) => void): void {
  for (const { dayType, bands: groupBands } of getBandGroups(bands)) {
    groupBands.forEach((band, index) => callback(band, dayType, index));
  }
}

export const getBandKey = (dayType: DayType, range: HourRange): BandKey =>
  `${dayType}-${range.start_hour}-${range.end_hour}`;

const BAND_KEY_RE = /^(WD|WE|ALL)-(\d+)-(\d+)$/;

export type ParsedBandKey = { dayType: DayType; range: HourRange };

/** Inverse of `getBandKey`. A band key is self-describing — it carries its own
 * day type and hour range — which is what lets a stored key be interpreted
 * without the price list it came from. */
export function parseBandKey(bandKey: string): ParsedBandKey | null {
  const match = BAND_KEY_RE.exec(bandKey);
  if (!match) return null;
  return {
    dayType: match[1] as DayType,
    range: { start_hour: Number(match[2]), end_hour: Number(match[3]) },
  };
}

/** Human label for a band key, wherever one is reported outside its own grid. */
export function formatBandKey(bandKey: BandKey): string {
  const parsed = parseBandKey(bandKey);
  return parsed ? getBandlabel(parsed.range, parsed.dayType) : bandKey;
}

export const getDateKey = (date: Date): DateKey => formatISO(date, { representation: 'date' }) as DateKey;

/** Every band of a set, keyed. Group order is WD then WE, or the single ALL
 * group — the order `getBandGroups` yields. */
export function getBandEntries<T extends HourRange>(bands: BandSet<T>): [BandKey, T, DayType][] {
  return getBandGroups(bands).flatMap(({ dayType, bands }) =>
    bands.map((band) => [getBandKey(dayType, band), band, dayType] as [BandKey, T, DayType]),
  );
}

/** The hours a band covers. `start_hour === end_hour` is the full day, and
 * `start_hour > end_hour` wraps midnight (Star FM 22:00–06:00). */
export function hoursInBand({ start_hour: start, end_hour: end }: HourRange): number[] {
  const span = start === end ? 24 : end > start ? end - start : 24 - start + end;
  return Array.from({ length: span }, (_, index) => (start + index) % 24);
}

/** Calendar report coordinates, with the placement weekday anchoring the start.
 * Hours after midnight belong to the following weekday, including Sunday → Monday. */
export function calendarHoursInBand(band: HourRange, weekday: IsoWeekday): { weekday: IsoWeekday; hour: number }[] {
  return hoursInBand(band).map((hour, index) => ({
    weekday: (((weekday - 1 + Math.floor((band.start_hour + index) / 24)) % 7) + 1) as IsoWeekday,
    hour,
  }));
}

export const getBandlabel = (range: HourRange, dayType?: DayType): string => {
  const start = range.start_hour !== null ? `${String(range.start_hour).padStart(2, '0')}:00` : ' - ';
  const endHour = range.end_hour === 0 ? 24 : range.end_hour;
  const end = endHour !== null ? `${String(endHour - 1).padStart(2, '0')}:59` : ' - ';
  const label = `${start} - ${end}`;
  if (dayType && dayType !== 'ALL') {
    return `${dayType} ${label}`;
  }
  return label;
};

/** Every present group carries at least one band — a split set needs both WD and
 * WE, an unsplit one needs ALL. */
export function hasBands<T>(bands: BandSet<T> | undefined): boolean {
  return bands ? getBandGroups(bands).every(({ bands: groupBands }) => groupBands.length > 0) : false;
}
